/** A crop region as fractions (0 to 1) of the image's width and height. */
export type CropRect = { x: number; y: number; width: number; height: number };

/** A clockwise turn in degrees (0, 90, 180 or 270), then a crop of the turned image. */
export type ImageEdit = { rotation: number; rect: CropRect };

const KEPT_TYPES = new Set(["image/png", "image/jpeg", "image/webp"]);

export async function loadImageSource(src: string): Promise<Blob> {
  const response = await fetch(src);
  if (!response.ok) throw new Error(`Failed to fetch image for editing: ${response.status}`);
  return response.blob();
}

/** Rotates the `source` image, then crops it, as a file named `name`, keeping PNG/JPEG/WebP formats and falling back to PNG. */
export async function editImage(
  source: Blob,
  { rotation, rect }: ImageEdit,
  name: string,
): Promise<File> {
  const image = await createImageBitmap(source);

  const isQuarterTurn = rotation % 180 !== 0;
  const turnedWidth = isQuarterTurn ? image.height : image.width;
  const turnedHeight = isQuarterTurn ? image.width : image.height;

  const sx = Math.round(rect.x * turnedWidth);
  const sy = Math.round(rect.y * turnedHeight);
  const width = Math.max(1, Math.round((rect.x + rect.width) * turnedWidth) - sx);
  const height = Math.max(1, Math.round((rect.y + rect.height) * turnedHeight) - sy);

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Canvas 2D context unavailable");
  context.translate(turnedWidth / 2 - sx, turnedHeight / 2 - sy);
  context.rotate((rotation * Math.PI) / 180);
  context.drawImage(image, -image.width / 2, -image.height / 2);
  image.close();

  const type = KEPT_TYPES.has(source.type) ? source.type : "image/png";
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (result) => (result ? resolve(result) : reject(new Error("Failed to encode edited image"))),
      type,
    );
  });
  return new File([blob], name, { type });
}
